import { isFiniteNumber } from './number';

/**
 * Randomizes the items of an array by reference.
 * @param sourceArray
 */
export function shuffle(sourceArray: Array<any>, seed?: number) {
  for (let i = 0; i < sourceArray.length - 1; i++) {
    const j = i + Math.floor((typeof seed === 'number' ? seed : Math.random()) * (sourceArray.length - i));

    const temp = sourceArray[j];
    sourceArray[j] = sourceArray[i];
    sourceArray[i] = temp;
  }

  return sourceArray;
}

/**
 * Generates an array of ascending numbers from 1 to n.
 *
 * @param {number} n - The number up to which the array should be filled.
 * @return {number[]} - An array filled with numbers from 1 to n in ascending order.
 *
 * @example
 * generateAscendingArray(3) -> Returns [1, 2, 3]
 *
 */
export const generateAscendingArray = (n: number): number[] => {
  const array: number[] = [];

  if (!n) return array;
  if (!isFiniteNumber(n)) return array;
  if (n < 1) return array;

  for (let i = 1; i <= n; i++) {
    array.push(i);
  }
  return array;
};

/**
 * Moves an item within an array to a new index.
 *
 * @param array
 * @param fromIndex
 * @param toIndex
 * @returns New array with the item moved to the new index.
 */
export const moveItemInArray = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  const newArray = [...array];
  if (!newArray[fromIndex] || !newArray[toIndex]) return newArray;

  const item = newArray.splice(fromIndex, 1)[0];
  newArray.splice(toIndex, 0, item);
  return newArray;
};

/**
 * Random pick an item from an array.
 */
export const randomPick = <T>(array: T[], seed?: number): T => {
  return array[Math.floor((typeof seed === 'number' ? seed : Math.random()) * array.length)];
};

/**
 * Composes a series of functions into a single function that applies them in sequence.
 *
 * @template T - The type of the argument and return value of the functions.
 * @param {...Array<(arg: T) => T>} fns - The functions to be composed.
 * @returns {(x: T) => T} - The composed function.
 */
export const pipe =
  <T>(...fns: Array<(arg: T) => T>) =>
  (x: T): T =>
    fns.reduce((acc, fn) => fn(acc), x);

/**
 * Convert a 2D array of strings to a CSV string.
 * @param {string[][]} sheetData - The 2D array of strings to convert.
 * @returns {string} The CSV string.
 */
export const convertToCSV = (sheetData: string[][]): string => {
  if (!sheetData) return '';

  return sheetData.map((row) => row.join(',')).join('\n');
};
